import {
  Component,
  OnInit,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
  ViewEncapsulation,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { WithdrawalService } from '../../share/service/withdrawal.service';
import { DialogService } from '../../share/service/dialog.service';
import { DialogRef } from '@angular/cdk/dialog';
import {
  getTransactionForProduct,
  getTransactionOptionsForProduct,
  hasAnyTransactions,
  hasTransactionForProduct,
  isEksProduct,
  isSameTransactionOption,
  isWithdrawalTransactionOption,
  resolveWithdrawalFlowType,
  toPendingWithdrawal,
  WithdrawalConfirmationData,
  WithdrawalFlowType,
  WithdrawalOffer,
  PendingWithdrawal,
  WithdrawalTransactionOption,
  WithdrawalViewState,
  WITHDRAWAL_PRODUCT_KREDIT,
} from './withdrawal-bank.config';

@Component({
  selector: 'app-withdrawal',
  templateUrl: './withdrawal.component.html',
  styleUrls: ['./withdrawal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WithdrawalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('confirmDialogContent') confirmDialogContent!: TemplateRef<unknown>;
  @ViewChild('confirmDialogActions') confirmDialogActions!: TemplateRef<unknown>;

  private dialogRef: DialogRef<string> | null = null;
  private pendingWithdrawal: PendingWithdrawal | null = null;
  public confirmationLabel = '';
  public isDialogSubmitting = false;

  public hasError = false;
  public isSubmitted = false;
  public isSubmitting = false;
  public errorType: 'validation' | 'api' | null = null;
  public confirmationData: WithdrawalConfirmationData | null = null;
  public isLoggedIn = false;
  public customerOffers: WithdrawalOffer[] = [];
  public availableTransactionOptions: WithdrawalTransactionOption[] = [];
  public shouldDisableFields = false;
  readonly withdrawalProductKredit = WITHDRAWAL_PRODUCT_KREDIT;

  private readonly ERROR_MESSAGES = {
    validation: 'Bitte ergänzen Sie die fehlenden Angaben.',
    api: 'Wir konnten zu Ihren Angaben keinen widerrufsberechtigten Auftrag finden. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
  };

  public withdrawalForm = this.fb.group({
    c24Id: [
      '' as string | WithdrawalTransactionOption | null,
      Validators.required,
    ],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    product: [WITHDRAWAL_PRODUCT_KREDIT, Validators.required],
    bankName: [''],
  });

  constructor(
    private fb: FormBuilder,
    private withdrawalService: WithdrawalService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
  ) {
    this.destroyRef.onDestroy(() => this.dialogRef?.close());
  }

  ngOnInit(): void {
    const c24session = window['wf_sso_c24session'];

    if (c24session) {
      this.isLoggedIn = true;
      this.loadCustomerInfo();
    }

    this.withdrawalForm
      .get('c24Id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isLoggedIn && this.customerOffers.length > 0) {
          this.updateFlowDependentFields();
        }
      });

    this.withdrawalForm
      .get('product')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isLoggedIn) {
          this.updateFlowDependentFields();
        }
      });

    this.withdrawalForm
      .get('bankName')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateFlowDependentFields();
      });

    this.updateFlowDependentFields();
  }

  onSubmit(): void {
    if (this.isSubmitting || !this.viewState.showInlineForm) {
      return;
    }

    this.clearError();

    if (this.withdrawalForm.invalid) {
      this.setError('validation');
      return;
    }

    const formValues = this.withdrawalForm.getRawValue();

    if (this.isLoggedIn) {
      this.openConfirmationForLoggedInUser();
      return;
    }

    this.validateAnonymousWithdrawal(formValues);
  }

  onConfirmClick(): void {
    if (this.isDialogSubmitting) {
      return;
    }

    this.isDialogSubmitting = true;
    this.cdr.detectChanges();

    const formValues = this.withdrawalForm.getRawValue();

    if (this.isLoggedIn) {
      const selected = this.getSelectedOption();

      if (!selected) {
        this.isDialogSubmitting = false;
        this.setError('validation');
        this.cdr.detectChanges();
        return;
      }

      this.submitWithdrawal(
        selected.withdrawalProductId,
        selected.transactionId,
        formValues,
      );
      return;
    }

    if (!this.pendingWithdrawal) {
      this.isDialogSubmitting = false;
      this.cdr.detectChanges();
      return;
    }

    this.submitWithdrawal(
      this.pendingWithdrawal.withdrawalProductId,
      this.pendingWithdrawal.transactionId,
      formValues,
    );
  }

  get applicationSelectorVariant(): 'select' | 'text' {
    return this.isLoggedIn && this.availableTransactionOptions.length > 0
      ? 'select'
      : 'text';
  }

  getErrorMessage(): string {
    if (!this.errorType) {
      return '';
    }

    return this.ERROR_MESSAGES[this.errorType];
  }

  get viewState(): WithdrawalViewState {
    return this.buildViewState();
  }

  private loadCustomerInfo(): void {
    this.withdrawalService
      .getCustomerInfo()
      .pipe(take(1), catchError(() => EMPTY), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const offers = response.offers ?? [];
          const hasNoOffers =
            offers.length === 0 || !hasAnyTransactions(offers);

          this.shouldDisableFields = hasNoOffers;

          if (hasNoOffers) {
            this.withdrawalForm.get('product')?.disable();
            this.withdrawalForm.get('c24Id')?.disable();
          } else {
            this.withdrawalForm.get('product')?.enable();
          }

          if (offers.length > 0) {
            this.customerOffers = offers;
            this.syncTransactionOptionsForProduct();
          }

          if (response.customerData) {
            this.withdrawalForm.patchValue({
              firstName: response.customerData.firstName || '',
              lastName: response.customerData.lastName || '',
              email: response.customerData.email || '',
            });
          }

          this.updateFlowDependentFields();
        },
      });
  }

  private openConfirmationForLoggedInUser(): void {
    const selected = this.getSelectedOption();

    if (!selected) {
      this.setError('validation');
      return;
    }

    this.confirmationLabel = selected.confirmationLabel;
    this.openConfirmationDialog();
  }

  private validateAnonymousWithdrawal(
    formValues: ReturnType<typeof this.withdrawalForm.getRawValue>,
  ): void {
    const product = formValues.product || WITHDRAWAL_PRODUCT_KREDIT;

    this.isSubmitting = true;

    this.withdrawalService
      .validateWithdrawal(
        typeof formValues.c24Id === 'string' ? formValues.c24Id : '',
        formValues.firstName || '',
        formValues.lastName || '',
        formValues.email || '',
        product,
      )
      .pipe(
        take(1),
        catchError((error) => {
          this.isSubmitting = false;
          if (error.status === 400) {
            this.setError('api');
          }
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          const pendingWithdrawal = toPendingWithdrawal(
            getTransactionForProduct(response, product),
          );

          if (!pendingWithdrawal) {
            this.setError('api');
            return;
          }

          this.pendingWithdrawal = pendingWithdrawal;
          this.confirmationLabel = pendingWithdrawal.label;
          this.openConfirmationDialog();
        },
      });
  }

  private submitWithdrawal(
    withdrawalProductId: string,
    transactionId: string,
    formValues: ReturnType<typeof this.withdrawalForm.getRawValue>,
  ): void {
    this.withdrawalService
      .submitWithdrawal(withdrawalProductId, transactionId)
      .pipe(
        catchError((error) => {
          this.isDialogSubmitting = false;
          this.cdr.detectChanges();
          if (error.status === 400) {
            this.hasError = true;
          }
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.isDialogSubmitting = false;
          this.handleSubmissionSuccess(formValues);
          this.cdr.detectChanges();
        },
      });
  }

  private handleSubmissionSuccess(
    formValues: ReturnType<typeof this.withdrawalForm.getRawValue>,
  ): void {
    this.hasError = false;

    const selected = this.getSelectedOption();

    this.confirmationData = {
      c24Id:
        selected?.withdrawalProductId ??
        (typeof formValues.c24Id === 'string' ? formValues.c24Id : ''),
      email: formValues.email ?? '',
      date: new Date().toLocaleDateString('de-DE'),
      time: new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      status: 'Eingereicht',
    };

    this.isSubmitted = true;
  }

  private openConfirmationDialog(): void {
    this.dialogRef = this.dialogService.open(
      'Bestätigung',
      this.confirmDialogContent,
      this.confirmDialogActions,
      {
        panelClass: 'withdrawal-dialog',
      },
    );
  }

  private getSelectedProduct(): string {
    return (
      this.withdrawalForm.get('product')?.value || WITHDRAWAL_PRODUCT_KREDIT
    );
  }

  private getSelectedOption(): WithdrawalTransactionOption | undefined {
    const value = this.withdrawalForm.get('c24Id')?.value;

    return isWithdrawalTransactionOption(value) ? value : undefined;
  }

  private getActiveBankGateway(): string | null {
    if (this.isLoggedIn) {
      return this.getSelectedOption()?.bankGateway ?? null;
    }

    return this.withdrawalForm.get('bankName')?.value || null;
  }

  private isSelectedProductUnavailable(): boolean {
    if (!this.isLoggedIn || this.shouldDisableFields) {
      return false;
    }

    const product = this.getSelectedProduct();

    return !hasTransactionForProduct(this.customerOffers, product);
  }

  private buildViewState(): WithdrawalViewState {
    const product = this.getSelectedProduct();
    const showNoOffersInfo =
      this.isLoggedIn &&
      (this.shouldDisableFields || this.isSelectedProductUnavailable());

    if (showNoOffersInfo) {
      return {
        flowType: 'no-offers',
        activeBankGateway: null,
        showBankSelector: false,
        showNoOffersInfo: true,
        showLoggedInApplicationSelector:
          !this.shouldDisableFields &&
          this.availableTransactionOptions.length > 0,
        showExternalRedirect: false,
        showInlineForm: false,
      };
    }

    if (isEksProduct(product)) {
      return this.buildEksViewState();
    }

    return this.buildBankDependentViewState();
  }

  private buildEksViewState(): WithdrawalViewState {
    const hasLoggedInApplications =
      this.isLoggedIn && this.availableTransactionOptions.length > 0;
    const selectedOption = this.getSelectedOption();
    const awaitingApplicationSelection =
      hasLoggedInApplications && !selectedOption;

    return {
      flowType: awaitingApplicationSelection ? 'none' : 'inline-form',
      activeBankGateway: null,
      showBankSelector: false,
      showNoOffersInfo: false,
      showLoggedInApplicationSelector: awaitingApplicationSelection,
      showExternalRedirect: false,
      showInlineForm: !awaitingApplicationSelection,
    };
  }

  private buildBankDependentViewState(): WithdrawalViewState {
    const showBankSelector = !this.isLoggedIn;
    const activeBankGateway = this.getActiveBankGateway();
    let flowType: WithdrawalFlowType =
      resolveWithdrawalFlowType(activeBankGateway);

    if (
      this.isLoggedIn &&
      this.availableTransactionOptions.length > 0 &&
      !activeBankGateway
    ) {
      flowType = 'none';
    }

    const showLoggedInApplicationSelector =
      this.isLoggedIn &&
      this.availableTransactionOptions.length > 0 &&
      flowType !== 'inline-form';

    return {
      flowType,
      activeBankGateway,
      showBankSelector,
      showNoOffersInfo: false,
      showLoggedInApplicationSelector,
      showExternalRedirect: flowType === 'external-redirect',
      showInlineForm: flowType === 'inline-form',
    };
  }

  private syncTransactionOptionsForProduct(): void {
    if (!this.isLoggedIn) {
      return;
    }

    const product = this.getSelectedProduct();
    this.availableTransactionOptions = getTransactionOptionsForProduct(
      this.customerOffers,
      product,
    );

    const current = this.getSelectedOption();
    const matchedOption = current
      ? this.availableTransactionOptions.find((option) =>
          isSameTransactionOption(option, current),
        )
      : undefined;

    if (matchedOption) {
      this.withdrawalForm.patchValue(
        { c24Id: matchedOption },
        { emitEvent: false },
      );
      return;
    }

    const nextValue =
      this.availableTransactionOptions.length === 1
        ? this.availableTransactionOptions[0]
        : null;

    this.withdrawalForm.patchValue({ c24Id: nextValue }, { emitEvent: false });
  }

  private updateFlowDependentFields(): void {
    if (this.isLoggedIn) {
      this.syncTransactionOptionsForProduct();
    }

    const product = this.getSelectedProduct();
    if (isEksProduct(product)) {
      this.withdrawalForm.patchValue({ bankName: '' }, { emitEvent: false });
    }

    const isInlineForm = this.viewState.showInlineForm;
    const inlineFormValidators: Record<string, ValidatorFn[]> = {
      c24Id: [Validators.required],
      firstName: [Validators.required],
      lastName: [Validators.required],
      email: [Validators.required, Validators.email],
    };

    Object.entries(inlineFormValidators).forEach(([field, validators]) => {
      const control = this.withdrawalForm.get(field);
      if (!control) {
        return;
      }

      if (isInlineForm) {
        control.setValidators(validators);
      } else {
        control.clearValidators();
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    const c24IdControl = this.withdrawalForm.get('c24Id');
    if (this.shouldDisableFields || this.isSelectedProductUnavailable()) {
      c24IdControl?.disable({ emitEvent: false });
    } else {
      c24IdControl?.enable({ emitEvent: false });
    }

    this.clearError();
  }

  private setError(errorType: 'validation' | 'api'): void {
    this.hasError = true;
    this.errorType = errorType;
  }

  private clearError(): void {
    this.hasError = false;
    this.errorType = null;
  }
}
